// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useEffect, useMemo, useState } from "react";
import { ACTION_TOOL_GLYPHS } from "@/features/tasks/built-in-task-icons";
import { cn } from "@/lib/utils";

/**
 * The `icon` column of `custom_task_appearances` holds one of two things: the name of a
 * `@mui/icons-material` export that the Action Tool resolves at runtime, or a raw SVG fragment it
 * drops into a 24×24 canvas. The preview honours both, and resolves names against the very same
 * Material glyphs — a Lucide look-alike would quietly misrepresent what the handheld draws. Only
 * the names the Action Tool can render are bundled (see `vite-plugins/material-icons.ts`).
 *
 * `getIconFromString` in `libs/tasks/src/lib/utils/use-action-task-types.tsx` is the behaviour
 * mirrored here, down to falling back to `Widgets` when a name does not resolve.
 *
 * A built-in task type is served the same way, except its name may also be one of the Action
 * Tool's own hand-drawn glyphs (`LowIcon`, `SpoiledIcon`, …), which resolve from
 * {@link ACTION_TOOL_GLYPHS} before the Material set is consulted.
 */

/** The name the Action Tool falls back to when a configured icon cannot be resolved. */
export const FALLBACK_ICON_NAME = "Widgets";

/**
 * The icon set, fetched once on first use.
 *
 * It lives in its own chunk, imported dynamically, and every consumer shares this single promise.
 */
let iconSetPromise: Promise<Record<string, string>> | null = null;
let iconSet: Record<string, string> | null = null;

function loadIconSet(): Promise<Record<string, string>> {
  if (!iconSetPromise) {
    iconSetPromise = import("virtual:material-icons")
      .then((module) => {
        iconSet = module.MATERIAL_ICON_MARKUP;
        return iconSet;
      })
      // Falling back to an empty set draws the cards without their glyphs rather than taking the
      // page down with it.
      .catch(() => {
        iconSet = {};
        return iconSet;
      });
  }
  return iconSetPromise;
}

/** Whether the stored value is an inline SVG fragment rather than a Material icon name. */
export function isInlineSvgIcon(icon: string | null | undefined): boolean {
  return !!icon && icon.trimStart().startsWith("<");
}

/**
 * The loaded Material icon set, or `null` until the chunk arrives.
 *
 * Used by the icon picker, which needs the full name list; the preview only needs one glyph and
 * gets it through {@link TaskIcon}.
 */
export function useMaterialIconSet(): Record<string, string> | null {
  const [set, setSet] = useState<Record<string, string> | null>(iconSet);

  useEffect(
    function fetchIconSet() {
      if (set) return;
      let cancelled = false;
      loadIconSet().then((loaded) => {
        if (!cancelled) setSet(loaded);
      });
      return () => {
        cancelled = true;
      };
    },
    [set],
  );

  return set;
}

/**
 * Resolve an icon name to its markup: a hand-drawn Action Tool glyph, else a Material icon, else
 * the fallback.
 *
 * A hand-drawn glyph needs no icon set, so it resolves before the chunk has arrived.
 */
export function materialIconMarkup(set: Record<string, string> | null, name: string | null | undefined): string | null {
  if (name && name in ACTION_TOOL_GLYPHS) return ACTION_TOOL_GLYPHS[name] ?? null;
  if (!set) return null;
  if (!name) return set[FALLBACK_ICON_NAME] ?? null;
  return set[name] ?? set[FALLBACK_ICON_NAME] ?? null;
}

/** Whether an icon of this name resolves to a glyph, once the set has loaded. */
export function isKnownMaterialIcon(set: Record<string, string> | null, name: string): boolean {
  if (name in ACTION_TOOL_GLYPHS) return true;
  if (!set) return true;
  return name in set;
}

/** SVG elements safe to render from stored configuration. */
const ALLOWED_TAGS = new Set([
  "svg",
  "g",
  "path",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
  "rect",
  "defs",
  "clipPath",
  "mask",
  "linearGradient",
  "radialGradient",
  "stop",
  "use",
  "title",
  "desc",
]);

/** Presentational attributes safe to keep. Anything else — `on*`, `href`, `style` — is dropped. */
const ALLOWED_ATTRIBUTES = new Set([
  "d",
  "fill",
  "fill-rule",
  "fill-opacity",
  "clip-rule",
  "clip-path",
  "stroke",
  "stroke-width",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-dasharray",
  "stroke-opacity",
  "opacity",
  "transform",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "x",
  "y",
  "x1",
  "x2",
  "y1",
  "y2",
  "width",
  "height",
  "points",
  "offset",
  "stop-color",
  "stop-opacity",
  "gradientUnits",
  "gradientTransform",
  "viewBox",
  "id",
]);

/**
 * Strip everything from a stored SVG fragment that is not a presentational shape.
 *
 * The fragment is editable from this very page, so it is treated as untrusted: scripts, event
 * handlers, external references and foreign objects are removed rather than rendered. Returns the
 * inner markup only — the caller supplies the wrapping `<svg>`.
 */
export function sanitizeSvgFragment(fragment: string): string {
  if (typeof window === "undefined") return "";
  const parsed = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${fragment}</svg>`,
    "image/svg+xml",
  );
  if (parsed.querySelector("parsererror")) return "";
  const root = parsed.documentElement;

  const scrub = (element: Element) => {
    for (const child of Array.from(element.children)) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        child.remove();
        continue;
      }
      for (const attribute of Array.from(child.attributes)) {
        const name = attribute.name.toLowerCase();
        // `use` may only reference a local id, never an external document
        if (name === "href" || name === "xlink:href") {
          if (child.tagName === "use" && attribute.value.startsWith("#")) continue;
          child.removeAttribute(attribute.name);
          continue;
        }
        if (!ALLOWED_ATTRIBUTES.has(attribute.name)) child.removeAttribute(attribute.name);
      }
      scrub(child);
    }
  };
  scrub(root);

  // A stored fragment may be a whole <svg> element; unwrap it so the caller's canvas wins
  const only = root.children.length === 1 ? root.children[0] : null;
  const source = only && only.tagName === "svg" ? only : root;
  return Array.from(source.children)
    .map((child) => new XMLSerializer().serializeToString(child))
    .join("");
}

interface TaskIconProps {
  /** The raw `icon` value: a Material icon name, an SVG fragment, or nothing. */
  icon?: string | null;
  /** Colour applied to the glyph — the markup fills with `currentColor`. */
  color?: string;
  className?: string;
}

/** Renders a task's icon exactly as the Action Tool would, for either kind of task. */
export function TaskIcon({ icon, color, className }: TaskIconProps) {
  const isInline = isInlineSvgIcon(icon);
  const set = useMaterialIconSet();

  const markup = useMemo(
    () => (isInline ? sanitizeSvgFragment(icon as string) : materialIconMarkup(set, icon)),
    [isInline, icon, set],
  );

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn("h-[22px] w-[22px] shrink-0", className)}
      style={{ color, fill: "currentColor" }}
      // Material markup comes from the build-time icon set; a stored fragment is sanitised above
      dangerouslySetInnerHTML={{ __html: markup ?? "" }}
    />
  );
}
